/**
 * Triveny.ai — "My day" home / launcher.
 *
 * Replaces PlatformDashboard for Triveny.ai users (config.isTriveny === true).
 * WeaveIn users continue to see the original PlatformDashboard untouched.
 *
 * Implements surface #1 from design_handoff_platform_redesign:
 *   - page-bar with mono eyebrow + Inter Tight title + right-aligned actions
 *   - greeting + role pill + summary line
 *   - Next-best-action (NBA) card with accent border
 *   - two-column grid: today's tasks (left), this-week stats + recent replies (right)
 *   - demoted "Jump to a module" chip row
 *
 * Triveny-7 refactor: this file used to carry its own `tokens`, `eyebrowStyle`,
 * `cardStyle`, `pillStyle()`, `buttonStyle()` helpers. Those are now shared
 * primitives in src/platform/shared/components/triveny/ so future Triveny
 * surfaces can reuse them without copy-paste.
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  DollarSign,
  Flag,
  Headphones,
  LayoutGrid,
  LayoutTemplate,
  Phone,
  Send,
  Target,
  Zap,
} from 'lucide-react';
import { config } from '@/src/config';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import {
  tvTokens,
  TvButton,
  TvCard,
  TvEyebrow,
  TvLabel,
  TvPageBar,
  TvPill,
  type TvVariant,
} from '@/src/platform/shared/components/triveny';

// ----------------------------------------------------------------------------
// Live data types (replaces the prior hardcoded TODAY_TASKS/THIS_WEEK/REPLIES
// mock arrays — now driven by /api/platform/sales/* + /api/platform/contacts
// scoped to the user's active org via X-CBO-ID + JWT cbo_id claim).
// ----------------------------------------------------------------------------

type Task = {
  time: string;
  title: string;
  meta: string;
  pill?: { label: string; variant: TvVariant };
  done?: boolean;
};

type Reply = { initials: string; name: string; preview: string; ago: string };

interface RawActivity {
  id: string;
  type?: string;
  title?: string;
  description?: string;
  contact?: string;
  due_date?: string | null;
  completed?: boolean;
  priority?: string;
  activity_date?: string | null;
  opportunity_id?: string;
}

interface RawOpportunity {
  id: string;
  name?: string;
  company?: string;
  amount?: number | string;
  stage?: string;
  close_date?: string | null;
  updated_at?: string | null;
}

interface RawContact {
  id: string | number;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  last_activity?: string | null;
  lifecycle_stage?: string;
  job_title?: string;
}

function todayIsoBounds(): { startOfTodayIso: string; endOfTodayIso: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return { startOfTodayIso: start.toISOString(), endOfTodayIso: end.toISOString() };
}

function formatTimeHM(iso?: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch {
    return '';
  }
}

function ago(iso?: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso).getTime();
    const diffSec = Math.max(0, Math.floor((Date.now() - d) / 1000));
    if (diffSec < 60) return `${diffSec}s`;
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
    return `${Math.floor(diffSec / 86400)}d`;
  } catch {
    return '';
  }
}

function initialsOf(first?: string, last?: string, company?: string): string {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (f || l) return `${(f[0] || '').toUpperCase()}${(l[0] || '').toUpperCase()}` || '??';
  const c = (company || '').trim();
  if (!c) return '??';
  const parts = c.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '??';
}

function formatMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

// Modules surfaced as a quiet "jump to" row (not the primary directory anymore).
const MODULES: Array<{ tab: string; label: string; icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }> }> = [
  { tab: 'platform-web-presence', label: 'Landing pages', icon: LayoutTemplate },
  { tab: 'platform-marketing', label: 'Campaigns', icon: Send },
  { tab: 'platform-sales', label: 'Pipeline', icon: Target },
  { tab: 'platform-revops', label: 'RevOps', icon: DollarSign },
  { tab: 'platform-support', label: 'Support', icon: Headphones },
];

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function todayLine(): string {
  const now = new Date();
  const day = now.toLocaleDateString(undefined, { weekday: 'short' });
  const month = now.toLocaleDateString(undefined, { month: 'short' });
  return `${day}, ${month} ${now.getDate()}`;
}

function greeting(): string {
  const hr = new Date().getHours();
  if (hr < 12) return 'Good morning';
  if (hr < 17) return 'Good afternoon';
  return 'Good evening';
}

function dispatchTabNav(tab: string) {
  // Reuses the existing platform-navigate custom event in app.tsx
  // (see the listener added in Phase 2 work).
  window.dispatchEvent(new CustomEvent('platform-navigate', { detail: { tab } }));
}

// Open the global command palette. The actual <CommandPalette> is mounted
// in app.tsx; we just emit an event for it. Mirrors the pattern already
// used by GlobalShortcuts (command-palette-mode events).
function openCommandPalette() {
  window.dispatchEvent(new CustomEvent('open-command-palette'));
}

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export default function TrivenyMyDay() {
  const { user } = useAuth();
  const userFirstName = (user?.name || (user as any)?.first_name || (user?.email?.split('@')[0]) || 'there')
    .toString()
    .split(/\s+/)[0];
  const orgRole = (user as any)?.tenant_name || (user as any)?.role || 'Triveny';

  // Live data from the demo tenant — scoped to the active org via X-CBO-ID +
  // JWT cbo_id claim. Loading states render the same UI shape so the layout
  // doesn't flicker (cards show empty rows until data arrives).
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [recentReplies, setRecentReplies] = useState<Reply[]>([]);
  const [thisWeek, setThisWeek] = useState<Array<[string, string]>>([
    ['—', 'Pipeline value'],
    ['0 / 0', 'Deals won'],
    ['0', 'Activities'],
    ['—', 'Avg deal size'],
  ]);

  useEffect(() => {
    let cancelled = false;
    const { startOfTodayIso, endOfTodayIso } = todayIsoBounds();
    (async () => {
      // Today's tasks — non-completed activities due today (or overdue).
      try {
        const res = await apiClient.get('/api/platform/sales/activities', {
          params: { page_size: 25, sort_by: 'due_date', sort_order: 'asc' },
        });
        if (cancelled) return;
        const rows: RawActivity[] = Array.isArray(res.data?.items)
          ? res.data.items
          : Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.activities)
              ? res.data.activities
              : [];
        const tasks: Task[] = rows
          .map((row): Task | null => {
            if (!row?.title) return null;
            const due = row.due_date || row.activity_date;
            const isCompleted = !!row.completed;
            const time = formatTimeHM(due);
            let pill: Task['pill'] | undefined;
            if (isCompleted) pill = { label: 'DONE', variant: 'success' };
            else if (due && new Date(due) < new Date(startOfTodayIso)) pill = { label: 'OVERDUE', variant: 'danger' };
            else if (due && new Date(due) <= new Date(endOfTodayIso)) pill = { label: 'DUE TODAY', variant: 'attention' };
            return {
              time: time || '—',
              title: row.title,
              meta: [row.type, row.contact].filter(Boolean).join(' · ') || row.description || '',
              pill,
              done: isCompleted,
            };
          })
          .filter((t): t is Task => !!t)
          .slice(0, 6);
        setTodayTasks(tasks);
      } catch {
        // Leave todayTasks empty on failure — surface no fake data.
      }

      // This week — derived from opportunities (pipeline value, wins) and
      // activities (volume). The platform endpoint already scopes by tenant.
      try {
        const [oppsRes, actsRes] = await Promise.all([
          apiClient.get('/api/platform/sales/opportunities', { params: { page_size: 200 } }),
          apiClient.get('/api/platform/sales/activities', { params: { page_size: 200 } }),
        ]);
        if (cancelled) return;
        const opps: RawOpportunity[] = Array.isArray(oppsRes.data?.items)
          ? oppsRes.data.items
          : Array.isArray(oppsRes.data)
            ? oppsRes.data
            : [];
        const acts: RawActivity[] = Array.isArray(actsRes.data?.items)
          ? actsRes.data.items
          : Array.isArray(actsRes.data)
            ? actsRes.data
            : [];
        const totalValue = opps.reduce((acc, o) => acc + Number(o.amount || 0), 0);
        const wonOpps = opps.filter((o) => (o.stage || '').toLowerCase() === 'closed_won');
        const wonValue = wonOpps.reduce((acc, o) => acc + Number(o.amount || 0), 0);
        const avgDeal = opps.length ? totalValue / opps.length : 0;
        setThisWeek([
          [formatMoney(totalValue), 'Pipeline value'],
          [`${wonOpps.length} / ${opps.length}`, 'Deals won'],
          [String(acts.length), 'Activities'],
          [avgDeal ? formatMoney(avgDeal) : '—', 'Avg deal size'],
        ]);
      } catch {
        /* leave defaults */
      }

      // Recent replies — proxy via contacts with recent last_activity. The
      // platform has no dedicated unified-inbox endpoint yet (TODO: wire to
      // workspace-messaging once it exposes a recent-threads endpoint).
      try {
        const res = await apiClient.get('/api/platform/contacts', {
          params: {
            sort_by: 'last_activity',
            sort_order: 'desc',
            page_size: 4,
          },
        });
        if (cancelled) return;
        const rows: RawContact[] = Array.isArray(res.data?.contacts)
          ? res.data.contacts
          : Array.isArray(res.data?.items)
            ? res.data.items
            : Array.isArray(res.data)
              ? res.data
              : [];
        const replies: Reply[] = rows.slice(0, 4).map((c) => ({
          initials: initialsOf(c.first_name, c.last_name, c.company_name),
          name: `${[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unknown'}${c.company_name ? ` · ${c.company_name}` : ''}`,
          preview: c.job_title
            ? `${c.job_title} — last activity ${ago(c.last_activity || null)} ago`
            : `Last activity ${ago(c.last_activity || null)} ago`,
          ago: ago(c.last_activity || null),
        }));
        setRecentReplies(replies);
      } catch {
        /* leave empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Summary counts derived from live data.
  const overdueCount = todayTasks.filter((t) => t.pill?.variant === 'danger').length;
  const doneCount = todayTasks.filter((t) => t.done).length;
  const openCount = todayTasks.length;
  const repliesCount = recentReplies.length;

  const nba = todayTasks.find((t) => t.pill?.variant === 'danger') ?? todayTasks[0];

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: tvTokens.paper,
        fontFamily: tvTokens.fontSans,
        color: tvTokens.ink,
      }}
      className="triveny-my-day"
    >
      {/* ─────────── Page bar ─────────── */}
      <TvPageBar
        eyebrow={<>Marketing &amp; Sales · Today · {todayLine()}</>}
        title="My day"
        actions={
          <>
            <TvButton variant="ghost" icon={LayoutGrid} onClick={openCommandPalette}>
              Modules
            </TvButton>
            <TvButton variant="ink" icon={Zap} onClick={openCommandPalette}>
              Quick actions
              <span
                style={{
                  marginLeft: 4,
                  paddingLeft: 8,
                  borderLeft: '1px solid rgba(255,255,255,0.18)',
                  fontFamily: tvTokens.fontMono,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  opacity: 0.7,
                }}
              >
                ⌘K
              </span>
            </TvButton>
          </>
        }
      />

      {/* ─────────── Body ─────────── */}
      <div style={{ flex: 1, padding: '20px 22px', overflow: 'auto' }}>
        {/* Greeting line */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
          <h1
            style={{
              fontFamily: tvTokens.fontDisplay,
              fontSize: 25,
              fontWeight: 600,
              letterSpacing: '-0.025em',
              margin: 0,
              color: tvTokens.ink,
              whiteSpace: 'nowrap',
            }}
          >
            {greeting()}, {userFirstName}.
          </h1>
          <TvPill variant="neutral">{orgRole}</TvPill>
        </div>
        <p style={{ fontSize: 13, color: tvTokens.ink500, margin: '0 0 18px' }}>
          {openCount} task{openCount === 1 ? '' : 's'} today
          {overdueCount > 0 && ` · ${overdueCount} overdue`}
          {doneCount > 0 && ` · ${doneCount} done`}
          {repliesCount > 0 && ` · ${repliesCount} unread replies`}
          .
        </p>

        {/* ─────────── NBA card (Start here) ─────────── */}
        {nba && (
          <TvCard accent style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 16px' }}>
              <span
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: tvTokens.r,
                  background: tvTokens.accent,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Flag size={18} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <TvEyebrow>Start here</TvEyebrow>
                <TvLabel size="lg" style={{ display: 'block', marginTop: 2 }}>
                  {nba.title}
                </TvLabel>
              </div>
              {nba.pill && <TvPill variant={nba.pill.variant}>{nba.pill.label}</TvPill>}
              <TvButton variant="primary" icon={Phone}>
                Start call
              </TvButton>
            </div>
          </TvCard>
        )}

        {/* ─────────── Two-column grid ─────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 1fr)',
            gap: 16,
          }}
          className="triveny-my-day-grid"
        >
          {/* Today's tasks */}
          <TvCard
            header={<TvLabel>Today's tasks</TvLabel>}
            headerActions={<TvPill>{openCount}</TvPill>}
          >
            {todayTasks.map((t, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 13,
                  padding: '11px 16px',
                  borderBottom: i < todayTasks.length - 1 ? `1px solid ${tvTokens.borderMuted}` : 0,
                }}
              >
                <span
                  style={{
                    fontFamily: tvTokens.fontMono,
                    fontSize: 11,
                    color: t.done ? tvTokens.success : tvTokens.ink400,
                    width: 36,
                  }}
                >
                  {t.time}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: t.done ? tvTokens.ink400 : tvTokens.ink,
                      textDecoration: t.done ? 'line-through' : 'none',
                    }}
                  >
                    {t.title}
                  </div>
                  <div style={{ fontSize: 11.5, color: tvTokens.ink500 }}>{t.meta}</div>
                </div>
                {t.pill && <TvPill variant={t.pill.variant}>{t.pill.label}</TvPill>}
              </div>
            ))}
          </TvCard>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* This week stats */}
            <TvCard header={<TvLabel>This week</TvLabel>}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 14,
                  padding: 16,
                }}
              >
                {thisWeek.map(([value, label], i) => (
                  <div key={i}>
                    <div
                      style={{
                        fontFamily: tvTokens.fontDisplay,
                        fontSize: 21,
                        fontWeight: 600,
                        letterSpacing: '-0.02em',
                        color: tvTokens.ink,
                      }}
                    >
                      {value}
                    </div>
                    <TvEyebrow style={{ marginTop: 2 }}>{label}</TvEyebrow>
                  </div>
                ))}
              </div>
            </TvCard>

            {/* Recent replies */}
            <TvCard
              header={<TvLabel>Recent replies</TvLabel>}
              headerActions={
                <button
                  type="button"
                  onClick={() => dispatchTabNav('platform-contacts')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: tvTokens.ink400,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                  }}
                  aria-label="Open inbox"
                >
                  <ArrowRight size={15} />
                </button>
              }
            >
              {recentReplies.map((m, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 11,
                    padding: '10px 16px',
                    borderBottom: i < recentReplies.length - 1 ? `1px solid ${tvTokens.borderMuted}` : 0,
                  }}
                >
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 999,
                      background: tvTokens.ink,
                      color: '#fff',
                      fontFamily: tvTokens.fontDisplay,
                      fontSize: 9,
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {m.initials}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: tvTokens.ink,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {m.name}
                      </span>
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontFamily: tvTokens.fontMono,
                          fontSize: 10,
                          color: tvTokens.ink400,
                        }}
                      >
                        {m.ago}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 11.5,
                        color: tvTokens.ink500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {m.preview}
                    </div>
                  </div>
                </div>
              ))}
            </TvCard>
          </div>
        </div>

        {/* ─────────── Demoted module launcher (chip row) ─────────── */}
        <div style={{ marginTop: 18 }}>
          <TvEyebrow style={{ marginBottom: 9 }}>Jump to a module</TvEyebrow>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.tab}
                  type="button"
                  onClick={() => dispatchTabNav(m.tab)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    height: 30,
                    padding: '0 13px',
                    borderRadius: tvTokens.r,
                    border: `1px solid ${tvTokens.border}`,
                    background: tvTokens.surface,
                    fontSize: 12.5,
                    color: tvTokens.ink700,
                    cursor: 'pointer',
                  }}
                >
                  <Icon size={14} style={{ color: tvTokens.ink400 }} />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Why this component exists separately from PlatformDashboard:
 *  - PlatformDashboard is the cross-tenant admin view used by WeaveIn super_admin
 *    / ops_admin / org_admin to manage many tenants. It's a directory, not a workspace.
 *  - "My day" is the operator workspace for an individual Triveny tenant user —
 *    different audience, different information density, different visual language
 *    (Triveny design system vs legacy Tailwind).
 *
 * Reachability: rendered by app.tsx when activeTab === 'platform-dashboard' AND
 * config.isTriveny. WeaveIn users continue to see <PlatformDashboard />.
 */

// Hint to bundlers: keep this referenced even if the import looks unused.
void config;
